import { logger } from '@/shared/utils';
import { fetchUsersInfo } from '@/features/users/utils';
import { createLogger } from '@/infrastructure/sync/utils';
import { IS_CONTACT_SYNC_ENABLED, IS_MIRRORFLY_TEAMS_ENABLED } from '@/processENV';
import { getFriendsList, EIM, SDK } from '@/infrastructure/sdk';
import { store } from '@/app/store';
import { setUserInfoMap } from '@/features/users/model';
import { setNotificationsEnabled } from '@/features/settings/model/settingsSlice';
import { setMutedUsersList } from '@/features/users/model/userInfoSlice';

const { log, logTime, flushLogs } = createLogger('ProfileSync');

const RETRY_DELAY_MS = 2000;

// ─── Helpers ────────────────────────────────────────────────────────────────

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// ─── Contact Sync ────────────────────────────────────────────────────────────

const syncContactUsers = async idsSet => {
   log('Step 0', 'Contact Sync enabled, fetching friends list...');

   const friendsRes = await getFriendsList();
   if (friendsRes?.statusCode !== 200 || !friendsRes?.data) return;

   const friends = friendsRes.users;
   const friendIds = friends.map(user => user.userId);

   friendIds.forEach(id => idsSet.add({ userId: id }));
   store.dispatch(setUserInfoMap(friends));

   log('Step 0', `Added ${friendIds.length} friends to sync list. Total: ${idsSet.size}`);
};

// ─── Teams Sync ─────────────────────────────────────────────────────────
const syncEnterpriseUsers = async idsSet => {
   log('Step 0', 'MirrorFly Teams Sync enabled, fetching teams user list...');

   let page = 1;
   const PAGE_SIZE = 100;

   while (true) {
      const enterpriseRes = await EIM.getEnterpriseUserList({ page, size: PAGE_SIZE });
      const enterpriseUsers = enterpriseRes?.users || [];

      if (enterpriseRes?.statusCode === 200 && enterpriseUsers.length > 0) {
         const enterpriseIds = enterpriseUsers.map(user => user.userId);
         enterpriseIds.forEach(id => idsSet.add({ userId: id }));
         store.dispatch(setUserInfoMap(enterpriseUsers));
         log('Step 0', `Added ${enterpriseIds.length} teams users from page ${page}. Total: ${idsSet.size}`);

         if (enterpriseUsers.length < PAGE_SIZE) {
            log('Step 0', 'All teams users fetched.');
            return;
         }

         page += 1;
      } else {
         log('Step 0', `Teams fetch failed or empty on page ${page}. Retrying...`);
         await delay(RETRY_DELAY_MS);
      }
   }
};

// ─── Sync Orchestrator ───────────────────────────────────────────────────────
export const runSyncCheck = async () => {
   const idsSet = new Set();

   if (IS_CONTACT_SYNC_ENABLED) await syncContactUsers(idsSet);
   if (IS_MIRRORFLY_TEAMS_ENABLED) await syncEnterpriseUsers(idsSet);

   const finalIds = Array.from(idsSet);

   return finalIds;
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Syncs user profiles for a set of user IDs.
 * @param {Set|Array} userIds - IDs of users to fetch profiles for.
 */
export const syncUserProfiles = async (userIds, options = {}) => {
   const { isSyncCheckNeeded = true } = options;
   const startTime = performance.now();

   log('Step 0', `🚀 Starting profile synchronization for ${userIds.length} users`);

   try {
      const finalIds = isSyncCheckNeeded ? await runSyncCheck() : [];

      if (!isSyncCheckNeeded && userIds.length > 0) {
         await fetchUsersInfo(userIds);
      }

      logTime('Summary', '✅ SYNC COMPLETE', startTime);
      return { totalCount: finalIds.length || userIds.length };
   } catch (error) {
      log('Error', `Profile sync failed: ${error.message}`);
      logger.error('Profile sync failed:', error);
      return { totalCount: 0, error: true };
   } finally {
      const totalDuration = ((performance.now() - startTime) / 1000).toFixed(2);
      flushLogs(`[ProfileSync] SYNC COMPLETE: UI RELEASED in ${totalDuration}s`);
   }
};

/**
 * Syncs user profiles for a set of user IDs if they don't already exist in the local store.
 * @param {String|Array} userIds - ID(s) of users to check and fetch profiles for.
 */
export const syncProfileIfNotExist = async userIds => {
   const ids = Array.isArray(userIds) ? userIds : [userIds];
   const state = store.getState();
   const localUsers = state.userInfoData.users || {};

   const missingIds = ids.filter(id => id && !localUsers[id]);

   if (missingIds.length > 0) {
      log('Step 0', `Checking local slice for ${ids.length} users. ${missingIds.length} missing. Syncing...`);
      return await syncUserProfiles(missingIds, { isSyncCheckNeeded: false });
   }

   log('Step 0', `Checking local slice for ${ids.length} users. All exist. Skipping sync.`);
   return { totalCount: 0, skipped: true };
};

export const syncMuteUsers = async () => {
   try {
      const res = await SDK.getMutedUsers();
      if (res?.statusCode === 200 && res?.data) {
         const { muteSettingStatus, mutedChats } = res.data;

         if (typeof muteSettingStatus === 'boolean') {
            // Mute set to true means notifications are disabled
            store.dispatch(setNotificationsEnabled(!muteSettingStatus));
         }

         if (Array.isArray(mutedChats)) {
            store.dispatch(setMutedUsersList(mutedChats));
         }
      }
   } catch (error) {
      log('Sync', 'Muted users sync failed', error);
   }
};
